import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useDispatch } from 'react-redux';
import { IRootDispatch } from 'stores/rematch/root-store';
import { UseFetchBookings, useFetchBookings } from './use-fetch-bookings';

const noop = () => {};
const asyncNoop = async () => Promise.resolve();

interface BookingsQueryContextType extends UseFetchBookings {
  hasFetchedBookingViews: boolean;
}

export const BookingsQueryContext = createContext<BookingsQueryContextType>({
  checkAllIndicator: false,
  fetchBookings: asyncNoop,
  fetchedViewIds: [],
  hasError: false,
  hasFetchedInitialViews: false,
  indeterminateCheck: false,
  onChangeCheckAllIndicator: noop,
  onChangeIndeterminateCheck: noop,
  onChangeShowActionSheet: noop,
  pageNumbers: {},
  pageSize: 20,
  pageTimestamps: {},
  resetViews: noop,
  showActionSheet: false,
  hasFetchedBookingViews: false,
});

interface BookingsViewProviderProps {
  children: React.ReactNode;
}

export const BookingsQueryProvider = ({ children }: BookingsViewProviderProps) => {
  const { bookingsStore } = useDispatch<IRootDispatch>();
  const doFetchBookingViews = bookingsStore.doFetchBookingViews;
  const [hasFetchedBookingViews, setHasFetchedBookingViews] = useState(false);
  const initialFetch = useRef(true);

  const {
    fetchBookings,
    hasFetchedInitialViews,
    fetchedViewIds,
    hasError,
    showActionSheet,
    indeterminateCheck,
    checkAllIndicator,
    pageNumbers,
    pageTimestamps,
    resetViews,
    pageSize,
    onChangeShowActionSheet,
    onChangeIndeterminateCheck,
    onChangeCheckAllIndicator,
  } = useFetchBookings();

  useEffect(() => {
    (async () => {
      await doFetchBookingViews();
      setHasFetchedBookingViews(true);
    })();
  }, [doFetchBookingViews]);

  /** handles initial fetch */
  useEffect(() => {
    (async () => {
      if (hasFetchedBookingViews) {
        if (initialFetch.current) {
          initialFetch.current = false;
          await fetchBookings({ initialFetch: true, caller: 'BookingsViewProvider.useEffect (initial)' }); // if the booking views have not been fetched, you will get two requests
        }
      }
    })();
  }, [fetchBookings, hasFetchedBookingViews]);

  const value: BookingsQueryContextType = useMemo(
    () => ({
      checkAllIndicator,
      fetchBookings,
      fetchedViewIds,
      hasError,
      hasFetchedInitialViews,
      indeterminateCheck,
      onChangeCheckAllIndicator,
      onChangeIndeterminateCheck,
      onChangeShowActionSheet,
      pageNumbers,
      pageSize,
      pageTimestamps,
      resetViews,
      showActionSheet,
      hasFetchedBookingViews,
    }),
    [
      checkAllIndicator,
      fetchBookings,
      fetchedViewIds,
      hasError,
      hasFetchedInitialViews,
      indeterminateCheck,
      onChangeCheckAllIndicator,
      onChangeIndeterminateCheck,
      onChangeShowActionSheet,
      pageNumbers,
      pageSize,
      pageTimestamps,
      resetViews,
      showActionSheet,
      hasFetchedBookingViews,
    ],
  );

  return <BookingsQueryContext.Provider value={value}>{children}</BookingsQueryContext.Provider>;
};

type Selector<T, Context> = (value: Context) => T;

export const useBookingsQuery = <T,>(selector: Selector<T, BookingsQueryContextType>) =>
  useContextSelector(BookingsQueryContext, selector);
